/**
 * Registrazione manuale di un movimento (SPEC-008 §5): stesso modulo, tre usi.
 *
 * 1. Dalla pagina principale del portafoglio, senza `fixedInstrument`: lo
 *    strumento si sceglie con `InstrumentPickerModal` (TODO.md) — tra quelli
 *    già a catalogo, o uno nuovo cercato/creato via OpenFIGI.
 * 2. Dal dettaglio di un titolo in portafoglio, con `fixedInstrument` già
 *    noto: non richiede alcuna scelta.
 * 3. Come "azione intrapresa" dal dettaglio di un'analisi AI, con
 *    `fixedInstrument`/`sourceAnalysisId` già fissati — l'utente registra solo
 *    cosa ha fatto davvero (data, prezzo, quantità), non lo strumento.
 */

import { type FormEvent, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { ORDER_TYPE_LABELS, TIME_IN_FORCE_LABELS, TX_TYPE_LABELS } from '@/lib/format';
import { Alert, Button, Field, Select } from '@/ui';
import { useCreateTransaction, type CreateTransactionInput, type PortfolioTransaction } from './api';
import { InstrumentPickerModal } from './InstrumentPickerModal';
import './portfolios.css';

const TX_TYPE_OPTIONS = Object.entries(TX_TYPE_LABELS).map(([value, label]) => ({ value, label }));
const ORDER_TYPE_OPTIONS = Object.entries(ORDER_TYPE_LABELS).map(([value, label]) => ({ value, label }));
const TIME_IN_FORCE_OPTIONS = Object.entries(TIME_IN_FORCE_LABELS).map(([value, label]) => ({ value, label }));

/** Stesso elenco di `portfolios.schema.ts::INSTRUMENT_TX_TYPES`, lato backend. */
const INSTRUMENT_TX_TYPES = new Set([
  'buy',
  'sell',
  'dividend',
  'savings_plan',
  'staking_reward',
  'transfer_in',
  'transfer_out',
  'split',
]);
const QUANTITY_TX_TYPES = new Set(['buy', 'sell']);

type TransactionFormProps = {
  portfolioId: string;
  /** Strumento già noto e non modificabile (azione intrapresa da un'analisi). */
  fixedInstrument?: { id: string; name: string };
  defaultTxType?: string;
  sourceAnalysisId?: string;
  defaultCurrency?: string;
  onSuccess?: (transaction: PortfolioTransaction) => void;
  onCancel?: () => void;
};

/** `datetime-local` non porta il fuso: si interpreta come ora locale del browser. */
function toIsoDateTime(localValue: string): string | null {
  if (!localValue) return null;
  const date = new Date(localValue);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** Adesso, nel formato che `<input type="datetime-local">` si aspetta (ora locale, niente fuso). */
function nowForDatetimeLocal(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function parseOptionalNumber(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** buy è sempre un'uscita, sell sempre un'entrata: l'utente non deve ricordarsi il segno. */
const SIGNED_TX_TYPES: Record<string, 1 | -1> = { buy: -1, sell: 1 };

/** Segno automatico per buy/sell, ma solo se non è già stato scritto esplicitamente (correzione manuale). */
function applyAutoSign(rawValue: string, txType: string): string {
  const sign = SIGNED_TX_TYPES[txType];
  if (!sign || rawValue.trim() === '' || rawValue.trim().startsWith('-')) return rawValue;
  const magnitude = Number(rawValue);
  if (!Number.isFinite(magnitude)) return rawValue;
  return sign < 0 ? `-${rawValue.trim()}` : rawValue;
}

function signedAmountFrom(quantity: number, price: number, txType: string): number {
  const magnitude = Math.abs(quantity * price);
  const sign = SIGNED_TX_TYPES[txType];
  return sign ? sign * magnitude : magnitude;
}

export function TransactionForm({
  portfolioId,
  fixedInstrument,
  defaultTxType = 'buy',
  sourceAnalysisId,
  defaultCurrency = 'EUR',
  onSuccess,
  onCancel,
}: TransactionFormProps) {
  const createTransaction = useCreateTransaction(portfolioId);

  const [txType, setTxType] = useState(defaultTxType);
  const [pickedInstrument, setPickedInstrument] = useState<{ id: string; name: string } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [txDate, setTxDate] = useState(nowForDatetimeLocal);
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [amount, setAmount] = useState('');
  // Diventa true alla prima modifica diretta dell'importo: da quel momento
  // quantità/prezzo non lo sovrascrivono più (correzione manuale rispettata).
  const [amountManuallyEdited, setAmountManuallyEdited] = useState(false);
  const [fees, setFees] = useState('');
  const [taxes, setTaxes] = useState('');
  const [currency, setCurrency] = useState(defaultCurrency);
  const [notes, setNotes] = useState('');
  const [orderType, setOrderType] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [takeProfitPrice, setTakeProfitPrice] = useState('');
  const [timeInForce, setTimeInForce] = useState('');

  const effectiveInstrument = fixedInstrument ?? pickedInstrument;
  const needsInstrument = INSTRUMENT_TX_TYPES.has(txType);
  // Area di scelta titolo: solo quando serve uno strumento e non è già fissato
  // dal chiamante (dettaglio titolo/analisi AI, punti 2-3 del commento sopra).
  const showInstrumentPicker = needsInstrument && !fixedInstrument;
  const needsQuantity = QUANTITY_TX_TYPES.has(txType);
  const showOrderDetails = orderType !== '';
  const showLimitPrice = orderType === 'limit' || orderType === 'stop_limit';
  const showStopPrice = orderType === 'stop' || orderType === 'stop_limit' || orderType === 'trailing_stop';

  const error = createTransaction.error instanceof ApiError ? createTransaction.error : null;
  const fieldError = (path: string) =>
    error?.isValidation ? error.details.find((detail) => detail.path.includes(path))?.message : undefined;

  /** Calcola l'importo da quantità × prezzo (con segno) — solo se non è già stato corretto a mano. */
  const recomputeAmount = (nextQuantity: string, nextPrice: string, nextTxType: string) => {
    if (amountManuallyEdited) return;
    const qty = parseOptionalNumber(nextQuantity);
    const prc = parseOptionalNumber(nextPrice);
    if (qty === undefined || prc === undefined) return;
    setAmount(String(signedAmountFrom(qty, prc, nextTxType)));
  };

  /**
   * Il contrario: da quantità + importo, il prezzo — solo se non è già stato
   * inserito. Innescato al blur (non a ogni tasto): derivarlo mentre l'utente
   * sta ancora scrivendo l'importo lo fisserebbe su un valore a metà digitato
   * (es. "-1" invece di "-100"), e da lì in poi il campo risulterebbe
   * "già inserito" e non più aggiornato.
   */
  const handleDerivePriceOnBlur = () => {
    if (price.trim() !== '') return;
    const qty = parseOptionalNumber(quantity);
    const amt = parseOptionalNumber(amount);
    if (!qty || amt === undefined) return;
    setPrice(String(Math.abs(amt / qty)));
  };

  const handleTxTypeChange = (value: string) => {
    setTxType(value);
    if (amountManuallyEdited) {
      setAmount((current) => applyAutoSign(current, value));
    } else {
      recomputeAmount(quantity, price, value);
    }
  };

  const handleQuantityChange = (value: string) => {
    setQuantity(value);
    recomputeAmount(value, price, txType);
  };

  const handlePriceChange = (value: string) => {
    setPrice(value);
    recomputeAmount(quantity, value, txType);
  };

  const handleAmountChange = (value: string) => {
    const signed = applyAutoSign(value, txType);
    setAmount(signed);
    setAmountManuallyEdited(true);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const isoDate = toIsoDateTime(txDate);
    if (!isoDate) return;

    const input: CreateTransactionInput = {
      txType,
      txDate: isoDate,
      amount: Number(amount),
      currency: currency.trim().toUpperCase(),
      quantity: parseOptionalNumber(quantity),
      price: parseOptionalNumber(price),
      fees: parseOptionalNumber(fees),
      taxes: parseOptionalNumber(taxes),
      notes: notes.trim() || undefined,
      orderType: orderType || undefined,
      limitPrice: parseOptionalNumber(limitPrice),
      stopPrice: parseOptionalNumber(stopPrice),
      takeProfitPrice: parseOptionalNumber(takeProfitPrice),
      timeInForce: timeInForce || undefined,
      sourceAnalysisId,
    };
    if (effectiveInstrument) {
      input.instrumentId = effectiveInstrument.id;
    }

    createTransaction.mutate(input, { onSuccess: (created) => onSuccess?.(created) });
  };

  return (
    <form className="transaction-form" onSubmit={handleSubmit}>
      <Select
        label="Tipo di movimento"
        value={txType}
        onChange={(event) => handleTxTypeChange(event.target.value)}
        options={TX_TYPE_OPTIONS}
        required
      />

      {fixedInstrument && (
        <p className="transaction-form__fixed-instrument">
          Strumento: <strong>{fixedInstrument.name}</strong>
        </p>
      )}

      {showInstrumentPicker && (
        <div className="transaction-form__picked-instrument">
          {pickedInstrument ? (
            <>
              <span>
                Strumento: <strong>{pickedInstrument.name}</strong>
              </span>
              <button type="button" className="transaction-form__change-instrument" onClick={() => setPickerOpen(true)}>
                Cambia titolo
              </button>
            </>
          ) : (
            <Button type="button" variant="secondary" onClick={() => setPickerOpen(true)}>
              Cerca titolo
            </Button>
          )}
          {fieldError('instrumentId') && <span className="field__error">{fieldError('instrumentId')}</span>}
        </div>
      )}

      {pickerOpen && (
        <InstrumentPickerModal
          onSelect={(instrument) => {
            setPickedInstrument(instrument);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <Field
        label="Data e ora"
        type="datetime-local"
        value={txDate}
        onChange={(event) => setTxDate(event.target.value)}
        required
        error={fieldError('txDate')}
      />

      {needsQuantity && (
        <Field
          label="Quantità"
          type="number"
          step="any"
          numeric
          value={quantity}
          onChange={(event) => handleQuantityChange(event.target.value)}
          onBlur={handleDerivePriceOnBlur}
          required={needsQuantity}
          error={fieldError('quantity')}
        />
      )}

      <Field
        label="Prezzo (opzionale)"
        hint="Se lo conosci insieme alla quantità, l'importo si calcola da solo"
        type="number"
        step="any"
        numeric
        value={price}
        onChange={(event) => handlePriceChange(event.target.value)}
        error={fieldError('price')}
      />

      <Field
        label="Importo"
        hint={
          SIGNED_TX_TYPES[txType]
            ? 'Segno applicato automaticamente per acquisto/vendita — modificabile a mano'
            : 'Negativo se in uscita, positivo se in entrata'
        }
        type="number"
        step="any"
        numeric
        value={amount}
        onChange={(event) => handleAmountChange(event.target.value)}
        onBlur={handleDerivePriceOnBlur}
        required
        error={fieldError('amount')}
      />

      <Field
        label="Commissioni (opzionale)"
        type="number"
        step="any"
        numeric
        value={fees}
        onChange={(event) => setFees(event.target.value)}
      />

      <Field
        label="Imposte (opzionale)"
        type="number"
        step="any"
        numeric
        value={taxes}
        onChange={(event) => setTaxes(event.target.value)}
      />

      <Field
        label="Valuta"
        value={currency}
        onChange={(event) => setCurrency(event.target.value)}
        placeholder="EUR"
        maxLength={3}
        required
        error={fieldError('currency')}
      />

      <Select
        label="Tipo di ordine (opzionale)"
        value={orderType}
        onChange={(event) => setOrderType(event.target.value)}
        options={ORDER_TYPE_OPTIONS}
        placeholder="Nessuno / non registrato"
      />

      {showOrderDetails && (
        <>
          {showLimitPrice && (
            <Field
              label="Prezzo limite"
              type="number"
              step="any"
              numeric
              value={limitPrice}
              onChange={(event) => setLimitPrice(event.target.value)}
            />
          )}
          {showStopPrice && (
            <Field
              label="Prezzo stop"
              type="number"
              step="any"
              numeric
              value={stopPrice}
              onChange={(event) => setStopPrice(event.target.value)}
            />
          )}
          <Field
            label="Take profit (opzionale)"
            type="number"
            step="any"
            numeric
            value={takeProfitPrice}
            onChange={(event) => setTakeProfitPrice(event.target.value)}
          />
          <Select
            label="Validità (opzionale)"
            value={timeInForce}
            onChange={(event) => setTimeInForce(event.target.value)}
            options={TIME_IN_FORCE_OPTIONS}
            placeholder="Non specificata"
          />
        </>
      )}

      <Field
        label="Note (opzionale)"
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        placeholder="Perché l'ho fatto"
      />

      <div className="transaction-form__actions">
        <Button type="submit" loading={createTransaction.isPending}>
          Registra
        </Button>
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Annulla
          </Button>
        )}
      </div>

      {error && !error.isValidation && (
        <Alert variant="error" title="Impossibile registrare il movimento">
          {error.message}
        </Alert>
      )}
    </form>
  );
}
